import { useEffect, useRef, useState } from 'react';
import { formatCurrency, formatPercent } from '@/utils/currency';
import {
  invoiceStatusLabels, invoiceStatusBadgeClasses,
} from './invoiceStatus';
import {
  canDelete, canEditCell, canTogglePaid, type GridField,
} from './cellEdit';
import {
  TextCell, NumberCell, DateCell, SelectCell, InvoiceContactCell, AttachmentCell, NotesCell, ATTACHMENT_ACCEPT,
} from './gridCells';
import type {
  Invoice, InvoiceDirection,
} from '@/types';
import { Loader2, Paperclip, Receipt, ArrowUpDown, ArrowUp, ArrowDown, Upload } from 'lucide-react';

/**
 * Hand-gerolltes Rechnungs-Grid ("verlinkte Excel"): Zeile = Rechnung,
 * Zellen autosave bei blur/change (Spiegel der EDITABLE-Whitelist).
 * Klick auf eine Zeile (außerhalb interaktiver Zellen) öffnet den
 * Detail-Drawer; "Weitere laden" hängt die nächste Seite an.
 */

/** Neue, noch nicht gespeicherte Zeile (Client-Draft, nicht persistiert). */
export interface InvoiceDraft {
  key: string;
  direction: InvoiceDirection;
  invoice_number: string;
  contact_id: number | null;
  contact_name: string | null;
  bauvorhaben_id: number | null;
  gewerk_id: number | null;
  amount_net: string;
  amount_tax: string;
  amount_gross: string;
  tax_rate_percent: string;
  invoice_date: string;
  due_date: string;
  description: string;
  notes: string;
  pendingFiles: File[];
  isCommitting: boolean;
  commitError: string;
}

export type SortValue =
  | 'invoice_date_desc' | 'invoice_date_asc'
  | 'due_date_asc' | 'due_date_desc'
  | 'number_asc' | 'number_desc'
  | 'amount_asc' | 'amount_desc'
  | 'contact_asc' | 'contact_desc'
  | 'bauvorhaben_asc' | 'bauvorhaben_desc'
  | 'gewerk_asc' | 'gewerk_desc';

export interface RechnungenGridProps {
  rows: Invoice[];
  drafts: InvoiceDraft[];
  bauvorhabenOptions: Array<{ id: number; name: string }>;
  gewerkOptions: Array<{ id: number; name: string }>;
  isAdmin: boolean;
  sort: SortValue;
  onSortChange: (next: SortValue) => void;
  /** Zellen-Save: payload kann mehrere Felder tragen (MwSt-Ableitung). */
  onSaveCell: (id: number, payload: Record<string, unknown>, fieldKeys: GridField[]) => void;
  onOpenDetail: (id: number) => void;
  onUploadFiles: (id: number, files: File[]) => void;
  onTogglePaid: (id: number, paid: boolean) => void;
  onDelete: (id: number) => void;
  onDraftChange: (key: string, patch: Partial<InvoiceDraft>) => void;
  onCancelDraft: (key: string) => void;
  cellSaving: Set<string>;
  cellErrors: Map<string, string>;
  hasMore: boolean;
  isLoadingMore: boolean;
  onLoadMore: () => void;
}

const cellKey = (id: number, field: string) => `${id}:${field}`;

/** Sortierbarer Spaltenkopf: erster Klick aufsteigend, zweiter absteigend,
 *  dritter wieder aufsteigend (Richtungspfeil zeigt die aktive Richtung). */
function Th({ label, sortAsc, sortDesc, activeSort, onSortChange, className = '' }: {
  label: string;
  sortAsc?: SortValue;
  sortDesc?: SortValue;
  activeSort?: SortValue;
  onSortChange?: (next: SortValue) => void;
  className?: string;
}) {
  const align = className.includes('text-right') ? 'text-right' : 'text-left';
  if (!sortAsc || !onSortChange) {
    return <th className={`px-2 py-2 ${align} text-xs font-semibold text-gray-600 ${className}`}>{label}</th>;
  }
  const isAsc = activeSort === sortAsc;
  const isDesc = sortDesc != null && activeSort === sortDesc;
  const active = isAsc || isDesc;
  // Aktive Auf-/Absteigung toggelt; sonst startet die Spalte aufsteigend.
  const next = isAsc && sortDesc != null ? sortDesc : sortAsc;
  return (
    <th className={`px-1 ${align} text-xs font-semibold ${className}`}>
      <button
        type="button"
        onClick={() => onSortChange(next)}
        className={`inline-flex items-center gap-1 rounded px-1 py-0.5 hover:bg-gray-100 ${active ? 'text-primary-700' : 'text-gray-600'}`}
      >
        {label}
        {isAsc ? (
          <ArrowUp className="h-3 w-3" />
        ) : isDesc ? (
          <ArrowDown className="h-3 w-3" />
        ) : (
          <ArrowUpDown className="h-3 w-3 opacity-30" />
        )}
      </button>
    </th>
  );
}

export function RechnungenGrid({
  rows, drafts, bauvorhabenOptions, gewerkOptions, isAdmin,
  sort, onSortChange,
  onSaveCell, onOpenDetail, onUploadFiles, onTogglePaid, onDelete,
  onDraftChange, onCancelDraft,
  cellSaving, cellErrors,
  hasMore, isLoadingMore, onLoadMore,
}: RechnungenGridProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const observerTarget = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = scrollRef.current;
    if (!root || !observerTarget.current || !hasMore) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) onLoadMore();
      },
      { root, threshold: 1 }
    );
    observer.observe(observerTarget.current);
    return () => observer.disconnect();
  }, [hasMore, onLoadMore]);

  const rowClick = (e: React.MouseEvent, id: number) => {
    if ((e.target as HTMLElement).closest('input,select,button,a,label')) return;
    onOpenDetail(id);
  };

  const moneyHeader = 'px-2 py-2 text-right text-xs font-semibold text-gray-600';

  return (
    <div ref={scrollRef} className="min-h-0 flex-1 overflow-auto">
      <table className="min-w-full border-separate border-spacing-0 text-sm">
        <thead className="sticky top-0 z-10 bg-gray-50">
          <tr className="border-b border-gray-200">
            <Th label="Rechnungsnummer" sortAsc="number_asc" sortDesc="number_desc" activeSort={sort} onSortChange={onSortChange} />
            <th className="px-2 py-2 text-left text-xs font-semibold text-gray-600">Richtung</th>
            <Th label="Datum" sortAsc="invoice_date_asc" sortDesc="invoice_date_desc" activeSort={sort} onSortChange={onSortChange} />
            <Th label="Fällig" sortAsc="due_date_asc" sortDesc="due_date_desc" activeSort={sort} onSortChange={onSortChange} />
            <Th label="Lieferant/Kunde" sortAsc="contact_asc" sortDesc="contact_desc" activeSort={sort} onSortChange={onSortChange} />
            <Th label="Bauvorhaben" sortAsc="bauvorhaben_asc" sortDesc="bauvorhaben_desc" activeSort={sort} onSortChange={onSortChange} />
            <Th label="Gewerk" sortAsc="gewerk_asc" sortDesc="gewerk_desc" activeSort={sort} onSortChange={onSortChange} />
            <th className="px-2 py-2 text-left text-xs font-semibold text-gray-600">Betreff</th>
            <th className="px-2 py-2 text-left text-xs font-semibold text-gray-600">Doku</th>
            <th className={moneyHeader}>Netto</th>
            <th className={moneyHeader}>MwSt-Satz</th>
            <th className={moneyHeader}>MwSt</th>
            <Th label="Brutto" sortAsc="amount_asc" sortDesc="amount_desc" activeSort={sort} onSortChange={onSortChange} className="text-right" />
            <th className="px-2 py-2 text-left text-xs font-semibold text-gray-600">Status</th>
            <th className="px-2 py-2 text-center text-xs font-semibold text-gray-600">Anlagen</th>
            <th className="px-2 py-2 text-center text-xs font-semibold text-gray-600">Bezahlt</th>
            <th className="px-2 py-2" />
          </tr>
        </thead>
        <tbody>
          {/* Client-Drafts (nicht persistiert) oben */}
          {drafts.map((draft) => (
            <DraftRow
              key={draft.key}
              draft={draft}
              bauvorhabenOptions={bauvorhabenOptions}
              gewerkOptions={gewerkOptions}
              onChange={onDraftChange}
              onCancel={() => onCancelDraft(draft.key)}
            />
          ))}
          {rows.map((inv) => (
            <tr key={inv.id} onClick={(e) => rowClick(e, inv.id)} className="cursor-pointer border-b border-gray-100 hover:bg-blue-50/40">
              <td className="px-1 py-0.5">
                <TextCell
                  value={inv.invoice_number}
                  editable={canEditCell(inv, 'invoice_number', isAdmin)}
                  saving={cellSaving.has(cellKey(inv.id, 'invoice_number'))}
                  error={cellErrors.get(cellKey(inv.id, 'invoice_number'))}
                  onSave={(next) => onSaveCell(inv.id, { invoice_number: next }, ['invoice_number'])}
                />
              </td>
              <td className="px-2 py-0.5">
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${inv.direction === 'eingang' ? 'bg-gray-100 text-gray-700' : 'bg-blue-100 text-blue-800'}`}>
                  {inv.direction === 'eingang' ? 'E' : 'R'}
                </span>
              </td>
              <td className="px-1 py-0.5">
                <DateCell
                  value={inv.invoice_date}
                  editable={canEditCell(inv, 'invoice_date', isAdmin)}
                  saving={cellSaving.has(cellKey(inv.id, 'invoice_date'))}
                  error={cellErrors.get(cellKey(inv.id, 'invoice_date'))}
                  onSave={(next) => next && onSaveCell(inv.id, { invoice_date: next }, ['invoice_date'])}
                />
              </td>
              <td className="px-1 py-0.5">
                <DateCell
                  value={inv.due_date ?? null}
                  editable={canEditCell(inv, 'due_date', isAdmin)}
                  saving={cellSaving.has(cellKey(inv.id, 'due_date'))}
                  error={cellErrors.get(cellKey(inv.id, 'due_date'))}
                  danger={inv.payment_status === 'offen' && !!inv.due_date && inv.due_date < todayIso()}
                  onSave={(next) => onSaveCell(inv.id, { due_date: next }, ['due_date'])}
                />
              </td>
              <td className="px-1 py-0.5">
                <InvoiceContactCell
                  contactId={inv.contact_id}
                  contactName={inv.contact_name}
                  editable={canEditCell(inv, 'contact_id', isAdmin)}
                  saving={cellSaving.has(cellKey(inv.id, 'contact_id'))}
                  error={cellErrors.get(cellKey(inv.id, 'contact_id'))}
                  onSelect={(id) => onSaveCell(inv.id, { contact_id: id }, ['contact_id'])}
                />
              </td>
              <td className="px-1 py-0.5">
                <SelectCell
                  value={inv.bauvorhaben_id}
                  options={bauvorhabenOptions}
                  editable={canEditCell(inv, 'bauvorhaben_id', isAdmin)}
                  saving={cellSaving.has(cellKey(inv.id, 'bauvorhaben_id'))}
                  error={cellErrors.get(cellKey(inv.id, 'bauvorhaben_id'))}
                  onSave={(next) => {
                    // BV-Wechsel setzt kein Gewerk zurück — Zuordnung ist
                    // bewusst unabhängig (Import kann Gewerk ohne BV setzen).
                    onSaveCell(inv.id, { bauvorhaben_id: next }, ['bauvorhaben_id']);
                  }}
                />
              </td>
              <td className="px-1 py-0.5">
                <SelectCell
                  value={inv.gewerk_id}
                  options={gewerkOptions}
                  editable={canEditCell(inv, 'gewerk_id', isAdmin)}
                  saving={cellSaving.has(cellKey(inv.id, 'gewerk_id'))}
                  error={cellErrors.get(cellKey(inv.id, 'gewerk_id'))}
                  onSave={(next) => onSaveCell(inv.id, { gewerk_id: next }, ['gewerk_id'])}
                />
              </td>
              <td className="px-1 py-0.5">
                <TextCell
                  value={inv.description ?? ''}
                  editable={canEditCell(inv, 'description', isAdmin)}
                  saving={cellSaving.has(cellKey(inv.id, 'description'))}
                  error={cellErrors.get(cellKey(inv.id, 'description'))}
                  onSave={(next) => onSaveCell(inv.id, { description: next || null }, ['description'])}
                />
              </td>
              <td className="px-1 py-0.5">
                <NotesCell
                  value={inv.notes ?? null}
                  editable={canEditCell(inv, 'notes', isAdmin)}
                  saving={cellSaving.has(cellKey(inv.id, 'notes'))}
                  error={cellErrors.get(cellKey(inv.id, 'notes'))}
                  onSave={(next) => onSaveCell(inv.id, { notes: next }, ['notes'])}
                />
              </td>
              <td className="px-1 py-0.5">
                <NumberCell
                  value={inv.amount_net ?? null}
                  editable={canEditCell(inv, 'amount_net', isAdmin)}
                  saving={cellSaving.has(cellKey(inv.id, 'amount_net'))}
                  error={cellErrors.get(cellKey(inv.id, 'amount_net'))}
                  onSave={(next) => onSaveCell(inv.id, { amount_net: next }, ['amount_net'])}
                />
              </td>
              <td className="px-1 py-0.5">
                <NumberCell
                  value={inv.tax_rate_percent ?? null}
                  format="percent"
                  editable={canEditCell(inv, 'tax_rate_percent', isAdmin)}
                  saving={cellSaving.has(cellKey(inv.id, 'tax_rate_percent'))}
                  error={cellErrors.get(cellKey(inv.id, 'tax_rate_percent'))}
                  onSave={(next) => onSaveCell(inv.id, { tax_rate_percent: next }, ['tax_rate_percent'])}
                />
              </td>
              <td className="px-1 py-0.5">
                <NumberCell
                  value={inv.amount_tax ?? null}
                  editable={canEditCell(inv, 'amount_tax', isAdmin)}
                  saving={cellSaving.has(cellKey(inv.id, 'amount_tax'))}
                  error={cellErrors.get(cellKey(inv.id, 'amount_tax'))}
                  title="MwSt wird automatisch berechnet (Brutto − Netto, je MwSt-Satz) und ist nicht direkt editierbar"
                  onSave={(next) => onSaveCell(inv.id, { amount_tax: next }, ['amount_tax'])}
                />
              </td>
              <td className="px-1 py-0.5">
                <NumberCell
                  value={inv.amount_gross}
                  bold
                  editable={canEditCell(inv, 'amount_gross', isAdmin)}
                  saving={cellSaving.has(cellKey(inv.id, 'amount_gross'))}
                  error={cellErrors.get(cellKey(inv.id, 'amount_gross'))}
                  onSave={(next) => onSaveCell(inv.id, { amount_gross: next }, ['amount_gross'])}
                />
              </td>
              <td className="px-2 py-0.5">
                <span className={`inline-block whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ${invoiceStatusBadgeClasses[inv.status]}`}>
                  {invoiceStatusLabels[inv.status]}
                </span>
              </td>
              <td className="px-2 py-0.5 text-center">
                <AttachmentCell
                  count={inv.attachment_count ?? (inv.has_file ? 1 : 0)}
                  editable={canEditCell(inv, 'file', isAdmin)}
                  saving={cellSaving.has(cellKey(inv.id, 'file'))}
                  error={cellErrors.get(cellKey(inv.id, 'file'))}
                  onUploadFiles={(files) => onUploadFiles(inv.id, files)}
                  onOpenDetail={() => onOpenDetail(inv.id)}
                />
              </td>
              <td className="px-2 py-0.5 text-center">
                {canTogglePaid(isAdmin) ? (
                  <input
                    type="checkbox"
                    checked={inv.payment_status === 'bezahlt'}
                    onChange={(e) => onTogglePaid(inv.id, e.target.checked)}
                    title={inv.payment_status === 'bezahlt' ? 'Als offen zurücksetzen' : 'Als bezahlt markieren'}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                ) : (
                  <span className={`text-xs ${inv.payment_status === 'bezahlt' ? 'text-green-700' : 'text-gray-400'}`}>
                    {inv.payment_status === 'bezahlt' ? '✓' : '—'}
                  </span>
                )}
              </td>
              <td className="px-2 py-0.5 text-right">
                {canDelete(inv, isAdmin) && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(inv.id);
                    }}
                    className="rounded p-1 text-xs text-gray-400 hover:bg-red-50 hover:text-red-600"
                    title="Rechnung löschen"
                  >
                    ✕
                  </button>
                )}
              </td>
            </tr>
          ))}
          {rows.length === 0 && drafts.length === 0 && (
            <tr>
              <td colSpan={17} className="py-12 text-center text-gray-500">
                <Receipt className="mx-auto mb-3 h-8 w-8 text-gray-400" />
                Keine Rechnungen gefunden
              </td>
            </tr>
          )}
        </tbody>
        {hasMore && (
          <tfoot>
            <tr>
              <td colSpan={17} className="py-3 text-center">
                {isLoadingMore ? (
                  <Loader2 className="mx-auto h-5 w-5 animate-spin text-primary-600" />
                ) : (
                  <button type="button" onClick={onLoadMore} className="text-sm text-primary-600 hover:text-primary-700">
                    Weitere laden
                  </button>
                )}
              </td>
            </tr>
          </tfoot>
        )}
      </table>
      <div ref={observerTarget} className="h-2" />
    </div>
  );
}

function todayIso(): string {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/** Client-Draft-Zeile: Pflichtfeld-Checks clientseitig, Commit automatisch,
 *  sobald Nummer + Brutto > 0 + Datum valid sind (Server verlangt alle drei). */
function DraftRow({
  draft, bauvorhabenOptions, gewerkOptions, onChange, onCancel,
}: {
  draft: InvoiceDraft;
  bauvorhabenOptions: Array<{ id: number; name: string }>;
  gewerkOptions: Array<{ id: number; name: string }>;
  onChange: (key: string, patch: Partial<InvoiceDraft>) => void;
  onCancel: () => void;
}) {
  // Pflichtfeld-Prüfung (Nummer, Brutto > 0, Datum) macht der Aufrufer beim
  // Patch — die Zeile bleibt bis zur Validität einfach stehen.

  return (
    <tr className="border-b border-amber-200 bg-amber-50/60">
      <td className="px-1 py-0.5">
        <TextCell
          value={draft.invoice_number}
          editable
          onSave={(next) => onChange(draft.key, { invoice_number: next })}
        />
      </td>
      <td className="px-2 py-0.5">
        <select
          value={draft.direction}
          onChange={(e) => onChange(draft.key, { direction: e.target.value as InvoiceDirection })}
          className="rounded border border-transparent bg-transparent py-0.5 text-xs hover:border-gray-300"
          title="Richtung (nach dem Speichern fixiert)"
        >
          <option value="eingang">E</option>
          <option value="ausgang">R</option>
        </select>
      </td>
      <td className="px-1 py-0.5">
        <DateCell
          value={draft.invoice_date || null}
          editable
          onSave={(next) => next && onChange(draft.key, { invoice_date: next })}
        />
      </td>
      <td className="px-1 py-0.5">
        <DateCell
          value={draft.due_date || null}
          editable
          onSave={(next) => onChange(draft.key, { due_date: next ?? '' })}
        />
      </td>
      <td className="px-1 py-0.5">
        <InvoiceContactCell
          contactId={draft.contact_id}
          contactName={draft.contact_name}
          editable
          onSelect={(id, contact) => {
            const name = contact
              ? contact.display_name
                || [contact.first_name, contact.last_name].filter(Boolean).join(' ')
                || contact.company_name
                || `Kontakt #${contact.id}`
              : null;
            onChange(draft.key, { contact_id: id, contact_name: id ? name : null });
          }}
        />
      </td>
      <td className="px-1 py-0.5">
        <SelectCell
          value={draft.bauvorhaben_id}
          options={bauvorhabenOptions}
          editable
          onSave={(next) => onChange(draft.key, { bauvorhaben_id: next })}
        />
      </td>
      <td className="px-1 py-0.5">
        <SelectCell
          value={draft.gewerk_id}
          options={gewerkOptions}
          editable
          onSave={(next) => onChange(draft.key, { gewerk_id: next })}
        />
      </td>
      <td className="px-1 py-0.5">
        <TextCell
          value={draft.description}
          editable
          onSave={(next) => onChange(draft.key, { description: next })}
        />
      </td>
      <td className="px-1 py-0.5">
        <NotesCell
          value={draft.notes || null}
          editable
          onSave={(next) => onChange(draft.key, { notes: next ?? '' })}
        />
      </td>
      <td className="px-1 py-0.5">
        <DraftAmountCell value={draft.amount_net} onChange={(v) => onChange(draft.key, { amount_net: v })} />
      </td>
      <td className="px-1 py-0.5">
        <DraftAmountCell value={draft.tax_rate_percent} onChange={(v) => onChange(draft.key, { tax_rate_percent: v })} format="percent" />
      </td>
      <td className="px-1 py-0.5">
        {/* MwSt wird aus Netto/Satz abgeleitet — im Draft read-only. */}
        <span
          className="block w-full text-right text-sm py-1 px-1 tabular-nums text-gray-600"
          title="MwSt wird automatisch berechnet (Brutto − Netto, je MwSt-Satz)"
        >
          {draft.amount_tax === '' ? <span className="text-gray-400">—</span> : formatCurrency(draft.amount_tax)}
        </span>
      </td>
      <td className="px-1 py-0.5">
        <DraftAmountCell value={draft.amount_gross} onChange={(v) => onChange(draft.key, { amount_gross: v })} bold />
      </td>
      <td className="px-2 py-0.5">
        <span className="inline-block rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">Neu</span>
      </td>
      <td className="px-2 py-0.5 text-center">
        <label
          title="Anlagen hochladen — werden nach dem Speichern hochgeladen (max. 10 MB je Datei)"
          className={`relative inline-flex cursor-pointer items-center justify-center rounded p-1 hover:bg-gray-100 ${
            draft.pendingFiles.length > 0 ? 'text-primary-600' : 'text-gray-400'
          }`}
        >
          {draft.pendingFiles.length > 0 ? <Paperclip className="h-4 w-4" /> : <Upload className="h-4 w-4" />}
          {draft.pendingFiles.length > 0 && (
            <span className="absolute -right-1.5 -top-1.5 rounded-full bg-primary-600 px-1 text-[10px] font-semibold leading-3 text-white">
              {draft.pendingFiles.length > 9 ? '9+' : draft.pendingFiles.length}
            </span>
          )}
          <input
            type="file"
            multiple
            accept={ATTACHMENT_ACCEPT}
            className="hidden"
            onChange={(e) => {
              const files = Array.from(e.target.files ?? []);
              if (files.length > 0) {
                onChange(draft.key, { pendingFiles: [...draft.pendingFiles, ...files] });
              }
              e.target.value = '';
            }}
          />
        </label>
        {draft.pendingFiles.length > 0 && (
          <div className="mt-0.5 flex flex-col gap-0.5">
            {draft.pendingFiles.map((file, index) => (
              <span key={`${file.name}-${index}`} className="flex items-center gap-1 text-[10px] text-gray-600">
                <span className="max-w-[90px] truncate">{file.name}</span>
                <button
                  type="button"
                  onClick={() => onChange(draft.key, { pendingFiles: draft.pendingFiles.filter((_, i) => i !== index) })}
                  className="text-gray-400 hover:text-red-600"
                  title="Anlage entfernen"
                >
                  ✕
                </button>
              </span>
            ))}
          </div>
        )}
      </td>
      <td className="px-2 py-0.5 text-center text-xs text-gray-400">—</td>
      <td className="px-2 py-0.5 text-right">
        {draft.isCommitting ? (
          <Loader2 className="mx-auto h-4 w-4 animate-spin text-primary-600" />
        ) : (
          <button
            type="button"
            onClick={onCancel}
            className="rounded p-1 text-xs text-gray-400 hover:bg-red-50 hover:text-red-600"
            title="Zeile verwerfen"
          >
            ✕
          </button>
        )}
      </td>
    </tr>
  );
}

/** Betrag/Satz-Zelle für Drafts: hält String-State, MwSt-Ableitung macht der Aufrufer. */
function DraftAmountCell({ value, onChange, bold = false, format = 'currency' }: {
  value: string;
  onChange: (next: string) => void;
  bold?: boolean;
  format?: 'currency' | 'percent';
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) {
      setDraft(value);
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing, value]);

  const commit = () => {
    setEditing(false);
    if (draft !== value) onChange(draft);
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        type="number"
        step="0.01"
        min="0"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') setEditing(false);
        }}
        className="input py-1 text-sm text-right w-24"
      />
    );
  }
  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      className={`block w-full text-right text-sm py-1 px-1 rounded tabular-nums hover:bg-gray-100 ${bold ? 'font-semibold' : ''}`}
    >
      {value === '' ? (
        <span className="text-gray-400">—</span>
      ) : format === 'percent' ? formatPercent(value) : formatCurrency(value)}
    </button>
  );
}